import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ImpersonationManager } from '../utils/impersonationManager';

/**
 * Custom hook for managing authenticated WebSocket connections
 * Automatically includes JWT token in the WebSocket connection
 * 
 * @param {string} baseUrl - The base WebSocket URL (without token)
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoConnect - Whether to connect automatically on mount
 * @param {function} options.onMessage - Callback function when a message is received
 * @param {function} options.onOpen - Callback function when connection is opened
 * @param {function} options.onClose - Callback function when connection is closed
 * @param {function} options.onError - Callback function when an error occurs
 * @param {boolean} options.includeToken - Whether to include JWT token in connection (default: true)
 * @param {number} options.reconnectDelay - Delay before reconnecting (default: 1000ms)
 * @param {number} options.maxReconnectAttempts - Maximum reconnection attempts (default: 5)
 * @param {boolean} options.enableReconnect - Enable automatic reconnection (default: true)
 * @returns {Object} WebSocket connection state and methods
 */
const useAuthenticatedWebSocket = (baseUrl, options = {}) => {
  const {
    autoConnect = true,
    onMessage = () => { },
    onOpen = () => { },
    onClose = () => { },
    onError = () => { },
    includeToken = true,
    reconnectDelay = 1000,
    maxReconnectAttempts = 5,
    enableReconnect = true,
    ensureSandboxReady = false,
    sandboxStatus = null,
  } = options;

  const { token, getAccessToken, isAuthenticated, registerWebSocketDisconnectCallback } = useAuth();

  // State management
  const [connectionState, setConnectionState] = useState({
    isConnected: false,
    isConnecting: false,
    error: null,
    lastMessage: null,
    reconnectAttempts: 0,
  });

  // Refs for stable references
  const socketRef = useRef(null);
  const closingSocketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const connectionTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const urlRef = useRef(baseUrl);
  const connectPromiseRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  // Store ALL options in refs so connect/disconnect don't need them as dependencies
  const optionsRef = useRef({
    includeToken,
    enableReconnect,
    reconnectDelay,
    maxReconnectAttempts,
    ensureSandboxReady,
    sandboxStatus,
  });
  useEffect(() => {
    optionsRef.current = {
      includeToken,
      enableReconnect,
      reconnectDelay,
      maxReconnectAttempts,
      ensureSandboxReady,
      sandboxStatus,
    };
  }, [includeToken, enableReconnect, reconnectDelay, maxReconnectAttempts, ensureSandboxReady, sandboxStatus]);

  // Store callbacks in refs to avoid unnecessary reconnections
  const callbacksRef = useRef({ onMessage, onOpen, onClose, onError });
  useEffect(() => {
    callbacksRef.current = { onMessage, onOpen, onClose, onError };
  }, [onMessage, onOpen, onClose, onError]);

  // Store auth functions in refs
  const authRef = useRef({ getAccessToken, isAuthenticated, token });
  useEffect(() => {
    authRef.current = { getAccessToken, isAuthenticated, token };
  }, [getAccessToken, isAuthenticated, token]);

  useEffect(() => {
    reconnectAttemptsRef.current = connectionState.reconnectAttempts;
  }, [connectionState.reconnectAttempts]);

  // Helper to update state safely
  const updateState = useCallback((updates) => {
    if (isMountedRef.current) {
      setConnectionState(prev => ({ ...prev, ...updates }));
    }
  }, []);

  // Clear all timeouts
  const clearTimeouts = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
  }, []);

  // Build WebSocket URL with authentication - uses refs, no external deps
  const buildAuthenticatedUrl = useCallback(async (url) => {
    if (!optionsRef.current.includeToken || !url) {
      return url;
    }

    const urlObj = new URL(url);

    try {
      const currentToken = await authRef.current.getAccessToken();
      if (!currentToken) {
        console.warn('No authentication token available for WebSocket connection');
        return url;
      }

      urlObj.searchParams.set('token', currentToken);

      const targetUserId = ImpersonationManager.getCurrentImpersonationUserId();
      if (targetUserId) {
        urlObj.searchParams.set('impersonate_user_id', targetUserId);
      }

      return urlObj.toString();
    } catch (err) {
      console.error('Error building authenticated URL:', err);
      return url;
    }
  }, []);

  // Setup WebSocket event handlers - uses refs, minimal deps
  const setupWebSocketHandlers = useCallback((ws, url) => {
    if (!ws) return;

    ws.onopen = (event) => {
      if (!isMountedRef.current) return;

      clearTimeouts();
      updateState({
        isConnected: true,
        isConnecting: false,
        error: null,
        reconnectAttempts: 0,
      });
      callbacksRef.current.onOpen(event);
    };

    ws.onmessage = (event) => {
      if (!isMountedRef.current) return;

      try {
        const message = JSON.parse(event.data);
        updateState({ lastMessage: message });
        callbacksRef.current.onMessage(message, event);
      } catch (err) {
        console.warn('Failed to parse WebSocket message as JSON:', err);
        updateState({ lastMessage: event.data });
        callbacksRef.current.onMessage(event.data, event);
      }
    };

    ws.onclose = (event) => {
      if (!isMountedRef.current) return;

      // Clear socket ref if this is still the current socket
      if (socketRef.current === ws) {
        socketRef.current = null;
      }
      
      updateState({
        isConnected: false,
        isConnecting: false,
      });
      callbacksRef.current.onClose(event);

      // Auto-reconnect logic
      const opts = optionsRef.current;
      const shouldReconnect = opts.enableReconnect &&
        event.code !== 1000 &&
        event.code !== 1001 &&
        reconnectAttemptsRef.current < opts.maxReconnectAttempts;

      if (shouldReconnect) {
        const delay = Math.min(opts.reconnectDelay * Math.pow(2, reconnectAttemptsRef.current), 30000);

        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current && urlRef.current) {
            updateState(prev => ({
              ...prev,
              reconnectAttempts: prev.reconnectAttempts + 1
            }));
            connectImpl();
          }
        }, delay);
      }
    };

    ws.onerror = (event) => {
      if (!isMountedRef.current) return;

      const errorInfo = {
        type: 'WebSocket Error',
        readyState: ws.readyState,
        readyStateText: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.readyState],
        url: url.replace(/token=[^&]+/, 'token=HIDDEN'),
        timestamp: new Date().toISOString(),
      };

      updateState({ error: errorInfo });
      callbacksRef.current.onError(errorInfo);
    };
  }, [clearTimeouts, updateState]);

  // Clean disconnect function - STABLE, no dependencies that change
  // NOTE: We intentionally do NOT clear connectPromiseRef here - that's only
  // cleared when the connect promise actually resolves. This prevents
  // React StrictMode double-invoke from creating duplicate connections.
  const disconnect = useCallback(() => {
    clearTimeouts();
    // DO NOT clear connectPromiseRef.current here!

    const ws = socketRef.current;
    if (ws) {
      // Track this socket as "closing" so connect() can wait for it
      closingSocketRef.current = ws;
      
      // Remove event handlers before closing
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      // Keep onclose to track when it's actually closed
      ws.onclose = () => {
        closingSocketRef.current = null;
      };

      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        try {
          ws.close(1000, 'Client disconnect');
        } catch (e) {
          // ignore
        }
      }

      socketRef.current = null;
    }

    updateState({
      isConnected: false,
      isConnecting: false,
      reconnectAttempts: 0,
    });
  }, [clearTimeouts, updateState]);

  // Internal connect implementation - called by stable connect function
  const connectImpl = async () => {
    const currentBaseUrl = urlRef.current;
    
    // Prevent duplicate connections (handles React StrictMode double-invoke)
    if (connectPromiseRef.current) {
      return connectPromiseRef.current;
    }

    // Check if already connected/connecting
    if (socketRef.current) {
      const state = socketRef.current.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        return Promise.resolve();
      }
    }

    // Validation
    if (!currentBaseUrl || !isMountedRef.current) {
      return Promise.reject(new Error('Invalid connection parameters'));
    }

    const opts = optionsRef.current;
    const auth = authRef.current;

    // Check authentication if required
    if (opts.includeToken && !auth.isAuthenticated && !auth.token) {
      updateState({
        error: {
          type: 'Authentication Error',
          message: 'User not authenticated'
        }
      });
      return Promise.reject(new Error('Authentication required'));
    }
    
    // Wait for sandbox to be ready if required
    if (opts.ensureSandboxReady && opts.sandboxStatus !== 'ready') {
      updateState({
        error: {
          type: 'Sandbox Not Ready',
          message: 'Waiting for sandbox to initialize'
        }
      });
      return Promise.reject(new Error('Sandbox not ready'));
    }

    // Create connection promise
    connectPromiseRef.current = (async () => {
      try {
        updateState({ isConnecting: true, error: null });

        // Wait for any socket that's in the process of closing
        if (closingSocketRef.current) {
          const closingSocket = closingSocketRef.current;
          await new Promise(resolve => {
            const checkClosed = () => {
              if (!closingSocketRef.current || closingSocket.readyState === WebSocket.CLOSED) {
                resolve();
              } else {
                setTimeout(checkClosed, 50);
              }
            };
            setTimeout(checkClosed, 50);
            setTimeout(resolve, 3000);
          });
        }

        // If there's an existing socket, close it first and wait
        if (socketRef.current) {
          const oldSocket = socketRef.current;
          
          oldSocket.onopen = null;
          oldSocket.onmessage = null;
          oldSocket.onclose = null;
          oldSocket.onerror = null;
          
          if (oldSocket.readyState === WebSocket.OPEN || oldSocket.readyState === WebSocket.CONNECTING) {
            oldSocket.close(1000, 'Replacing connection');
            await new Promise(resolve => {
              const checkClosed = () => {
                if (oldSocket.readyState === WebSocket.CLOSED) {
                  resolve();
                } else {
                  setTimeout(checkClosed, 50);
                }
              };
              setTimeout(checkClosed, 50);
              setTimeout(resolve, 2000);
            });
          }
          socketRef.current = null;
        }

        // Build authenticated URL
        const authenticatedUrl = await buildAuthenticatedUrl(currentBaseUrl);

        // Create WebSocket
        let ws;
        try {
          ws = new WebSocket(authenticatedUrl);
        } catch (err) {
          console.error('Failed to create WebSocket:', err);
          throw new Error(`Failed to create WebSocket: ${err.message}`);
        }

        // Store reference immediately
        socketRef.current = ws;

        // Setup handlers
        setupWebSocketHandlers(ws, authenticatedUrl);

        // Set connection timeout
        connectionTimeoutRef.current = setTimeout(() => {
          if (ws.readyState === WebSocket.CONNECTING) {
            ws.close();
            updateState({
              isConnecting: false,
              error: {
                type: 'Connection Timeout',
                message: 'Failed to connect within 10 seconds'
              }
            });
          }
        }, 10000);

        // Wait for connection to open or fail
        return new Promise((resolve) => {
          const originalOnOpen = ws.onopen;
          const originalOnError = ws.onerror;

          ws.onopen = (event) => {
            originalOnOpen?.(event);
            resolve();
          };

          ws.onerror = (event) => {
            originalOnError?.(event);
            resolve();
          };
        });

      } catch (err) {
        console.error('Connection error:', err);
        updateState({
          isConnecting: false,
          error: {
            type: 'Connection Error',
            message: err.message
          }
        });
        throw err;
      } finally {
        connectPromiseRef.current = null;
      }
    })();

    return connectPromiseRef.current;
  };

  // STABLE connect function - only depends on baseUrl
  const connect = useCallback(() => {
    return connectImpl();
  }, [baseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Send message function
  const sendMessage = useCallback((data) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.warn('Cannot send message: WebSocket not connected');
      return false;
    }

    try {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      socketRef.current.send(message);
      return true;
    } catch (err) {
      console.error('Error sending WebSocket message:', err);
      return false;
    }
  }, []);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    updateState({ reconnectAttempts: 0 });
    disconnect();
    setTimeout(() => {
      if (isMountedRef.current) {
        connectImpl().catch(() => {});
      }
    }, 100);
  }, [disconnect, updateState]);

  // Test connection without authentication
  const testConnection = useCallback(async () => {
    try {
      const testWs = new WebSocket(baseUrl);

      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          testWs.close();
          reject(new Error('Connection test timeout'));
        }, 5000);

        testWs.onopen = () => {
          clearTimeout(timeout);
          testWs.close();
          resolve(true);
        };

        testWs.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Test connection failed'));
        };

        testWs.onclose = (event) => {
          clearTimeout(timeout);
          if (event.code !== 1000) {
            reject(new Error(`Connection closed with code: ${event.code}`));
          }
        };
      });
    } catch (err) {
      console.error('Test connection error:', err);
      throw err;
    }
  }, [baseUrl]);

  // Track component mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Track URL changes
  useEffect(() => {
    urlRef.current = baseUrl;
  }, [baseUrl]);

  // Register disconnect callback with AuthContext for impersonation changes
  useEffect(() => {
    if (registerWebSocketDisconnectCallback) {
      const unregister = registerWebSocketDisconnectCallback(disconnect);
      return unregister;
    }
  }, [registerWebSocketDisconnectCallback, disconnect]);

  // SINGLE effect for auto-connect and cleanup
  useEffect(() => {
    if (autoConnect && baseUrl) {
      connectImpl().catch(() => {});
    }

    return () => {
      clearTimeouts();
      
      const ws = socketRef.current;
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onclose = null;
        ws.onerror = null;
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, 'Cleanup');
        }
        socketRef.current = null;
      }
    };
  }, [autoConnect, baseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect to monitor sandbox status and auto-retry connection when ready
  useEffect(() => {
    if (ensureSandboxReady && sandboxStatus === 'ready' && !socketRef.current) {
      setTimeout(() => {
        if (!socketRef.current && !connectionState.isConnecting) {
          connectImpl().catch(() => {});
        }
      }, 100);
    }
  }, [sandboxStatus, ensureSandboxReady, connectionState.isConnecting]);

  return {
    // Connection state
    isConnected: connectionState.isConnected,
    isConnecting: connectionState.isConnecting,
    error: connectionState.error,
    lastMessage: connectionState.lastMessage,
    reconnectAttempts: connectionState.reconnectAttempts,

    // Methods - these are now STABLE references
    sendMessage,
    connect,
    disconnect,
    reconnect,
    testConnection,

    // Auth state
    isAuthenticated,
  };
};

export default useAuthenticatedWebSocket;
